import {APP_INITIALIZER, LOCALE_ID, NgModule} from '@angular/core';

import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {CookieModule} from 'ngx-cookie';
import {CommonModule, registerLocaleData} from '@angular/common';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {ApiModule} from '@tj/tj-dmp-lib-api';
import localeFr from '@angular/common/locales/fr';
import localeEnGb from '@angular/common/locales/en-GB';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {UnauthorizedHttpInterceptor} from './interceptor/UnauthorizedHttpInterceptor';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {TRANSLATE_HTTP_LOADER_CONFIG} from '@ngx-translate/http-loader';
import {DmpAdminModule} from './admin/admin.module';
import {DmpLoginModule} from './login/login.module';
import {LangInterceptor} from './interceptor/lang.interceptor';
import {MonacoEditorModule} from 'ngx-monaco-editor-v2';
import {I18nHttpLoaderFactory} from './i18n-http-loader-factory';
import {ErrorsInterceptor} from './interceptor/errors.interceptor';
import {CacheInterceptor} from './interceptor/cache.interceptor';
import {ToastModule} from 'primeng/toast';
import {providePrimeNG} from 'primeng/config';
import Aura from '@primeng/themes/aura';
import {KeycloakService} from 'keycloak-angular';
import {KeycloakInit} from './admin/auth/keycloak-init.factory';
import {LangService} from './admin/services/lang.service';
import {TranslateInit} from './admin/services/translate-init.factory';
import {ComponentsModule} from './admin/components/components.module';
import {AuthInterceptor} from './admin/auth/authInterceptor.interceptor';
import {AuthStateService} from "./admin/auth/auth-state.service";

registerLocaleData(localeFr, 'fr');
registerLocaleData(localeEnGb, 'en-GB');

// DMP Lib API
sessionStorage.overrideDmpUrl = '/services';

/* Tableaux : p-table et p-treeTable partagent un seul rendu, celui du thème legacy
   (lib-ext/primeng-theme.css) : en-tête gris, texte gris foncé, cellules de 1rem, filets clairs.
   Ce thème habille les p-table parce que son sélecteur `.p-datatable .p-datatable-thead > tr > th`
   est plus spécifique que celui d'Aura. Pour le p-treeTable, les deux sélecteurs ont la même
   spécificité et Aura, injecté après, l'emporte : l'arbre des abonnements avait un en-tête blanc et
   des cellules plus serrées. Les mêmes valeurs sont posées sur les deux composants, pour que le rendu
   ne dépende plus de l'ordre des feuilles de style (cf. docs/ui-design-guidelines.md §15). */
const TABLE_CELLS = {
    headerCell: {background: '#f8f9fa', color: '#495057', borderColor: '#e9ecef', padding: '1rem 1rem'},
    row: {color: '#495057'},
    bodyCell: {borderColor: '#e9ecef', padding: '1rem 1rem'},
};

const withTableCells = (component: any) => ({
    ...component,
    headerCell: {...component.headerCell, ...TABLE_CELLS.headerCell},
    row: {...component.row, ...TABLE_CELLS.row},
    bodyCell: {...component.bodyCell, ...TABLE_CELLS.bodyCell},
});

const AuraPinkPreset = {
    ...Aura,
    semantic: {
        ...(Aura as any).semantic,
        primary: {
            50: '{pink.50}',
            100: '{pink.100}',
            200: '{pink.200}',
            300: '{pink.300}',
            400: '{pink.400}',
            500: '{pink.500}',
            600: '{pink.600}',
            700: '{pink.700}',
            800: '{pink.800}',
            900: '{pink.900}',
            950: '{pink.950}'
        }
    },
    components: {
        ...(Aura as any).components,
        // Aura (comme Lara, Nora et Material) demande 700 pour les tags. Rendu avec Inter, la
        // police du showcase PrimeNG, ça passe ; avec Poppins, dont la Bold est très lourde, le
        // tag écrase le reste de la ligne. On ajuste le jeton plutôt que de surcharger .p-tag en
        // CSS : la géométrie et les couleurs natives sont conservées.
        tag: {
            ...(Aura as any).components.tag,
            root: {
                ...(Aura as any).components.tag.root,
                fontWeight: '500'
            }
        },
        datatable: withTableCells((Aura as any).components.datatable),
        treetable: withTableCells((Aura as any).components.treetable)
    }
};

@NgModule({ bootstrap: [AppComponent],
    declarations: [AppComponent],
    exports: [AppComponent], imports: [BrowserAnimationsModule,
        CommonModule,
        AppRoutingModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: I18nHttpLoaderFactory,
                deps: [HttpClient]
            },
            isolate: false
        }),
        CookieModule.forRoot(),
        ApiModule,
        FontAwesomeModule,
        DmpAdminModule,
        DmpLoginModule,
        MonacoEditorModule.forRoot(),
        ToastModule,
        ComponentsModule], providers: [
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
        { provide: LOCALE_ID, useValue: 'fr' },
        {
            provide: TRANSLATE_HTTP_LOADER_CONFIG,
            useValue: {
                prefix: './assets/i18n/',
                suffix: '.json',
                enforceLoading: false,
                useHttpBackend: true,
            },
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: UnauthorizedHttpInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LangInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ErrorsInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: CacheInterceptor,
            multi: true,
        },
        KeycloakService,
        AuthStateService,
        {
            provide: APP_INITIALIZER,
            useFactory: KeycloakInit,
            multi: true,
            deps: [KeycloakService, AuthStateService],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: TranslateInit,
            multi: true,
            deps: [LangService],
        },
        providePrimeNG({
            theme: {
                preset: AuraPinkPreset as any,
                options: {
                    darkModeSelector: false
                }
            },
            // Panneaux des listes (select, multiselect, autocomplete, treeselect…) rendus dans `body`
            // par défaut : laissés dans leur conteneur, ils étaient coupés par le défilement du
            // contenu des modales. Le datepicker n'utilise pas `p-overlay` : `appendTo` reste à poser
            // sur chaque `<p-datepicker>` (cf. docs/ui-design-guidelines.md §1.5).
            overlayOptions: {
                appendTo: 'body'
            }
        }),
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule {}
